/**
 * Shows a warning when an account-based pension's annual payment is below the legislated minimum
 * drawdown for the owner's age. Used in Client Profile (Assets step).
 *
 * IT NO LONGER FAILS SILENTLY (owner-reported, 2026-08-31). The check needs the owner's age; with no
 * date of birth on file there is no age, and this component simply rendered nothing — so a partner's
 * pension showed no warning while the client's did, with nothing on screen to say the check had not
 * been made. Silence is indistinguishable from a pass.
 *
 * The rule itself lives in pensionMinimum.ts, pure and tested. This renders its answer.
 */
import React from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { formatCurrency } from '../utils/index.ts';
import { checkPensionMinimum } from './pensionMinimum.ts';
import type { FactFindAsset } from '../../../shared/src/calculation/factFindTypes.ts';

interface PensionMinimumWarningProps {
    asset: FactFindAsset;
    ownerAge: number | null | undefined;
    /** Whose pension it is, for the "we can't check this yet" message. */
    ownerName?: string;
}

const PensionMinimumWarning = ({ asset, ownerAge, ownerName }: PensionMinimumWarningProps) => {
    if (!asset) return null;
    const result = checkPensionMinimum(asset.value, asset.income, ownerAge, ownerName || 'the owner');

    if (result.state === 'below') {
        return (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-100 p-2 text-xs text-amber-800">
                <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <span>
                    Warning: The annual payment is below the required minimum of{' '}
                    <strong>{formatCurrency(result.minimumPayment)}</strong> (which is{' '}
                    {(result.rate * 100).toFixed(2)}% for age {result.age}).
                </span>
            </div>
        );
    }

    if (result.state === 'unknown-age') {
        /*
         * The state that used to be invisible. A balance and a payment are entered, so the client
         * expects this checked — and the reason we cannot is something they can fix in one step.
         */
        return (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                <HelpCircle size={18} className="mt-0.5 flex-shrink-0 text-slate-400" />
                <span>
                    We can&apos;t check this against the minimum drawdown until we know {result.who}&apos;s date of
                    birth — the minimum depends on age. Add it in <strong>Your Details</strong>.
                </span>
            </div>
        );
    }

    return null;
};

export default PensionMinimumWarning;
